/**
 * StopList - The full stop list container with drag-and-drop reordering.
 *
 * Layout (top to bottom):
 *   1. Non-draggable "Depart Yard" row
 *   2. Draggable stop rows (via react-beautiful-dnd)
 *   3. Non-draggable "Return to Yard" row
 *   4. "Add Stop" button
 *
 * Props:
 *   stops        {Array}    - ordered stop array
 *   selectedStop {object}   - currently selected stop (or null)
 *   onSelectStop {function} - called with stop object when a row is clicked
 *   onReorder    {function} - called with new stops array after a drag
 *   onAddStop    {function} - called when "Add Stop" is clicked
 *   yardName     {string}   - yard / depot label (default: "Yard")
 *   leaveAt      {Date}     - departure time from yard (for display)
 */

import React from 'react'
import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Box from '@material-ui/core/Box'
import AddIcon from '@material-ui/icons/Add'
import HomeIcon from '@material-ui/icons/Home'

import StopListItem from './StopListItem'

const { reorderStops, formatTime } = require('./routeHelpers')

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  yardRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
  },
  yardIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  yardLabel: {
    flex: 1,
    fontWeight: 500,
    fontSize: '0.85rem',
  },
  yardTime: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  droppableArea: {
    minHeight: 40,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
    color: theme.palette.text.disabled,
  },
  emptyStateText: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  addButtonRow: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  addButton: {
    width: '100%',
  },
}))

function YardRow({ label, time, isReturn }) {
  const classes = useStyles()
  return (
    <Box className={classes.yardRow}>
      <HomeIcon fontSize="small" className={classes.yardIcon} />
      <Typography className={classes.yardLabel}>
        {isReturn ? `Return — ${label}` : `Depart — ${label}`}
      </Typography>
      {time ? (
        <Typography className={classes.yardTime}>{formatTime(time)}</Typography>
      ) : null}
    </Box>
  )
}

function StopList({
  stops = [],
  selectedStop = null,
  onSelectStop,
  onReorder,
  onAddStop,
  yardName = 'Yard',
  leaveAt,
  endLegMinutes = null,
}) {
  const classes = useStyles()

  const handleDragEnd = (result) => {
    if (!result.destination) return
    if (result.destination.index === result.source.index) return

    const reordered = reorderStops(stops, result.source.index, result.destination.index)
    if (onReorder) onReorder(reordered)
  }

  // Estimated return time = last stop departure + drive back to yard
  const lastStop = stops.length > 0 ? stops[stops.length - 1] : null
  const returnTime = (() => {
    if (!lastStop?.estimatedDeparture) return null
    const departure = new Date(lastStop.estimatedDeparture).getTime()
    const driveBack = (endLegMinutes != null && endLegMinutes > 0) ? endLegMinutes * 60000 : 0
    return new Date(departure + driveBack)
  })()

  return (
    <Paper className={classes.root} elevation={0}>
      <Box className={classes.scrollArea}>
        {/* Non-draggable: Yard departure */}
        <YardRow label={yardName} time={leaveAt} isReturn={false} />

        {stops.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2" className={classes.emptyStateText}>
              No stops added yet.
              <br />
              Use "Add Stop" to build the route.
            </Typography>
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="stop-list">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classes.droppableArea}
                >
                  {stops.map((stop, index) => (
                    <StopListItem
                      key={stop._id ? String(stop._id) : `stop-${index}`}
                      stop={stop}
                      index={index}
                      isSelected={
                        selectedStop
                          ? String(selectedStop._id) === String(stop._id)
                          : false
                      }
                      onSelect={onSelectStop}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        <Divider className={classes.divider} />

        {/* Non-draggable: Return to yard */}
        <YardRow label={yardName} time={returnTime} isReturn />
      </Box>

      {/* Add Stop button */}
      <Box className={classes.addButtonRow}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onAddStop}
          className={classes.addButton}
        >
          Add Stop
        </Button>
      </Box>
    </Paper>
  )
}

export default StopList
